import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Dictionary } from 'lodash';
import { Button } from '@bringg/react-components';
import {
	User,
	Location,
	Team,
	ServiceArea,
	VehicleType,
	Vehicle,
	OPTIMIZATION_TYPE,
	DeliveryBlock,
	CustomUnitsDefinition,
	ParkingSpot,
	CustomerAvailabilityHour,
	CustomAttributeRes
} from '@bringg/types';
import {
	DataChangesNotification,
	RoutesPlannerDeps,
	RoutesPlannerApp,
	RoutesPlannerTask,
	ConsecutiveRoutes,
	RoutesPlannerConfiguration,
	RoutesPlannerBreak,
	OpenUserAssignerProps,
	UpdateVehicleOnRunProps
} from '@bringg-frontend/routes-planner';
import { EtaResponseRouteItem, FullEtaRequest, EtaResponse } from '@bringg-frontend/eta-calculator';
import type { RoutesPlannerRun } from '@bringg-frontend/routes-planner';
import { Directions } from '@bringg/types/types/directions';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import classNames from 'classnames';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import { ManualOptimizationItem } from './models';
import { prepareDataForOptimization } from './routes-planner-manager-logic';
import useEvents from './routes-planner-events/use-events';
import { hasFeatureFlag } from '../../utils/feature-flags';
import { configurationService } from 'bringg-web/features/routes-planner/configuration-service';
import { openOrderPage } from '../../utils/tasks-utils';

interface Props {
	users: Dictionary<User>;
	tasks: RoutesPlannerTask[];
	routes: Dictionary<RoutesPlannerRun>;
	consecutiveRoutes: ConsecutiveRoutes;
	routeInfoByIndex: Dictionary<EtaResponseRouteItem>;
	breaks?: Dictionary<RoutesPlannerBreak[]>;
	deliveryBlocks: DeliveryBlock[];
	directions?: Directions[];
	team: Partial<Team>;
	getAllServiceAreas?: () => Promise<ServiceArea[]>;
	mapApiKey: string;
	optimizationType: OPTIMIZATION_TYPE;
	isManual: boolean;
	currentUser: User;
	configuration: RoutesPlannerConfiguration;
	onCancel: () => void;
	onApply: (
		changes: ManualOptimizationItem[],
		modifiedTasks: Partial<Bringg.Task>[],
		errorHandle: () => void
	) => void;
	optimizationRequestId: string;
	fromDispatch: boolean;
	vehicleTypeByVehicleIds: Dictionary<VehicleType>;
	openSupportModal: () => void;
	deps: RoutesPlannerDeps;
	openUserAssignerModal: (props: OpenUserAssignerProps) => void;
	openVehicleAssignerModal?: (
		runId: number,
		onClose: ({ runId, vehicleId, tasksIds }: UpdateVehicleOnRunProps) => void
	) => void;
	vehicles: Vehicle[];
	customerIdByTaskInventory: { [key: string]: number };
	customUnitsDefinitions?: CustomUnitsDefinition;
	parkingSpots?: ParkingSpot[];
	isReadOnly: boolean;
	customerAvailabilityHours: { [customerId: string]: CustomerAvailabilityHour[] };
	customAttributes: CustomAttributeRes[];
}

const RoutesPlannerManager = ({
	mapApiKey,
	routes,
	tasks,
	routeInfoByIndex,
	users,
	breaks,
	deliveryBlocks,
	directions,
	team,
	getAllServiceAreas,
	optimizationType,
	isManual,
	currentUser,
	configuration,
	onApply,
	onCancel,
	optimizationRequestId,
	fromDispatch,
	vehicleTypeByVehicleIds,
	openSupportModal,
	deps,
	vehicles,
	openUserAssignerModal,
	openVehicleAssignerModal,
	customerIdByTaskInventory,
	customUnitsDefinitions,
	consecutiveRoutes,
	parkingSpots,
	isReadOnly,
	customerAvailabilityHours,
	customAttributes
}: Props) => {
	const { t } = useTranslation();
	const shouldListenToEventsOnPlanning = hasFeatureFlag(currentUser, 'route_planner_realtime_events');
	const isOptimizationKpiEnabled = hasFeatureFlag(currentUser, 'enable_optimization_kpi');
	const { realtimeData } = useEvents(tasks, fromDispatch || shouldListenToEventsOnPlanning, customUnitsDefinitions);
	const [changes, setChanges] = useState<DataChangesNotification>(null);
	const [isApplyingChangesLoading, setIsApplyingChangesLoading] = useState(false);

	const homeLocation: Location = useMemo(() => {
		return team?.lat && team.lat ? ({ lat: team.lat, lng: team.lng } as Location) : null;
	}, [team]);

	const applyChanges = useCallback((): void => {
		if (isReadOnly) {
			return;
		}

		const changesForOptimization = prepareDataForOptimization(
			changes.runToTasks,
			changes.runToBreaks,
			changes.runToDriver,
			changes.routeInfoByIndex,
			routeInfoByIndex,
			optimizationType,
			isManual
		);
		if (!optimizationRequestId) {
			setIsApplyingChangesLoading(true);
		}

		onApply(changesForOptimization, changes.modifiedTasks, () => setIsApplyingChangesLoading(false));
	}, [changes, onApply, optimizationType]);

	const recalculateEtaFetch = useCallback(async (etaRequest: FullEtaRequest): Promise<EtaResponse> => {
		const { sdk } = getRootEnv().dashboardSdk;
		return sdk.application.executeAction('calculate_planned_eta', etaRequest, { timeout: 100000 });
	}, []);

	return (
		<div className={classNames('routes-planner-manager-container', { 'read-only-mode': isReadOnly })}>
			<RoutesPlannerApp
				mapApiKey={mapApiKey}
				home={homeLocation}
				drivers={users}
				tasks={tasks}
				breaks={breaks}
				deliveryBlocks={deliveryBlocks}
				directions={directions}
				routes={routes}
				consecutiveRoutes={consecutiveRoutes}
				mapZoom={configurationService.MAP_DEFAULT_ZOOM}
				configuration={configuration}
				onDataChanged={setChanges}
				openOrderPage={openOrderPage}
				tFunction={t}
				parkingSpots={parkingSpots}
				optimizationType={optimizationType}
				currentUser={currentUser}
				onItemMoveRecalculateEtaFetch={recalculateEtaFetch}
				team={team}
				getAllServiceAreas={getAllServiceAreas}
				optimizationRequestId={optimizationRequestId}
				fromDispatch={fromDispatch}
				realtimeData={realtimeData}
				openUserAssignerModal={openUserAssignerModal}
				openVehicleAssignerModal={openVehicleAssignerModal}
				vehicleTypeByVehicleIds={vehicleTypeByVehicleIds}
				onContactSupport={openSupportModal}
				deps={deps}
				vehicles={vehicles}
				customerIdByTaskInventory={customerIdByTaskInventory}
				isReadOnly={isReadOnly}
				customerAvailabilityHours={customerAvailabilityHours}
				isOptimizationKpiEnabled={isOptimizationKpiEnabled}
				customAttributes={customAttributes}
			/>
			<div className="routes-planner-footer">
				<Button
					data-test-id="routes-planner-close-btn"
					className="routes-planner-close-btn"
					onClick={onCancel}
					disabled={isApplyingChangesLoading}
				>
					{t('OPTIMIZATION_PREVIEW.CLOSE')}
				</Button>
				<Button
					data-test-id="routes-planner-apply-btn"
					className=" routes-planner-apply-btn"
					onClick={applyChanges}
					disabled={!changes || isApplyingChangesLoading}
					type="primary"
					icon={
						isApplyingChangesLoading && (
							<BringgIcon
								iconName={BringgFontIcons.Loader}
								spin
								data-test-id="routes-lanner-apply-loader"
							/>
						)
					}
				>
					{t('OPTIMIZATION_PREVIEW.APPLY')}
				</Button>
			</div>
		</div>
	);
};

export default React.memo(RoutesPlannerManager, () => true);
